import { ImageSizeTypeEnum, ImageTypeNameEnum } from '@inter-ikea-kompis/enums';
import {
  IProduct,
  IProductContent,
  IProductImage,
  IProductItem,
  IShoppingItem,
  IShoppingProduct
} from '@inter-ikea-kompis/types';
import { captureException } from '@sentry/react';
import { DoorHeightEnum } from 'CoreAPI/Components/DoorHeightEnum';
import { DoorFrameColorEnum, getDoorFrameSize, isProductADoorFrame } from 'products/doorframes';
import { getRailSize, isProductARail } from 'products/rails';
import { getSprId } from 'redirects';
import { IShoppingProductsMap, IStringMap, IVisibleProducts, SprCodes } from 'utils/constants';

const productId2ShoppingItem = (productId: string): IShoppingItem => {
  const [type, id] = productId.split('-');
  return {
    type,
    id,
    quantity: 1
  };
};

export const product2ShoppingProduct = (product: IProduct): IShoppingProduct => {
  return {
    quantity: 1,
    product
  };
};

export const shoppingProduct2Product = (product: IShoppingProduct): IProduct => {
  return product.product;
};

export const shoppingProduct2ProductItem = (product: IShoppingProduct): IProductItem => {
  const [type, id] = product.product.itemId.split('-');
  return {
    type,
    id
  };
};

export const shoppingProduct2ShoppingItem = (product: IShoppingProduct): IShoppingItem => {
  const type = product.product.content?.ruItemType;
  const id = product.product.content?.ruItemNo;
  if (type && id) {
    return {
      type,
      id,
      quantity: product.quantity
    };
  }

  return productId2ShoppingItem(product.product.itemId);
};

export const shoppingItem2ProductId = ({ id, type }: IShoppingItem): string => {
  return `${type}-${id}`;
};

export const productId2ShoppingProduct = (products: IShoppingProductsMap) => {
  return (id: string) => {
    return products[id];
  };
};

export const getVpcForSpr = (product: IProduct) => {
  const sprId = getSprId(product.itemId);

  return sprId && SprCodes[sprId];
};

const DexfTypes: IStringMap = {
  '37246': 'CHR',
  '37247': 'CHR',
  '37254': 'Fitting',
  '37253': 'Fitting',
  '37252': 'Fitting',
  '37245': 'Frame',
  '30904': 'Panel',
  '37879': 'StopBar',
  '37878': 'StopBar',
  '37267': 'Track'
};

export const getDexfType = (typeCode: string) => {
  return DexfTypes[typeCode] || 'Unknown';
};

export const shoppingProducts2NormalizedTypes = (shoppingProducts: IShoppingProduct[]) => {
  return <IVisibleProducts>shoppingProducts.reduce<IVisibleProducts>((acc, value) => {
    const type = getDexfType(value.product.content?.typeCode as string);

    const product = {
      quantity: value.quantity,
      id: value.product.itemId
    };

    if (acc[type]) {
      acc[type] = [...acc[type], ...[product]];
    } else {
      acc[type] = [product];
    }

    return acc;
  }, {});
};

const MANDATORY_PRODUCTS = [
  // FITTING PACKAGES
  'ART-10500064', // SKYTTA fitt pack f 2 trck rail
  'ART-60500066', // SKYTTA fitt pack f middle dr
  'ART-80500065', // SKYTTA fitt pack f 3 trck rail

  // CHR
  'ART-40500067', // SKYTTA ceiling height reducer 2 tracks
  'ART-00511096', // SKYTTA ceiling height reducer 3 tracks
  'ART-20500068', // SKYTTA top cover panel paintable beige

  // STOPBARS
  'ART-00513236', // SKYTTA stop bar 2 tracks s2 white
  'ART-80513237' // SKYTTA stop bar 3 tracks s2 white
];

// FRAMES AND RAILS
const TOTAL_NR_OF_COLORS = 3;
const REQUIRED_NUMBER_OF_COLORS_AVAILABLE = 1; // We used to have 2 but NZ pushed us to only have 1

export const getMandatoryProductsData = (allProducts: IShoppingProductsMap) => {
  const validationData = Object.values(allProducts).reduce(
    (acc, shoppingProduct) => {
      const { valid, itemId } = shoppingProduct.product;
      const { lowDoorColors, highDoorColors, railColors } = acc.missingSkyttaProducts;

      if (!valid) {
        // MANDATORY_PRODUCTS
        const invalidProductIsMandatory = MANDATORY_PRODUCTS.find(id => {
          return id === itemId;
        });

        if (invalidProductIsMandatory) {
          acc.invalidMandatories.push(shoppingProduct);
        }

        // Probably [, currentId] if its index 1 but it feels prone to errors
        // eslint-disable-next-line prefer-destructuring
        const currentId = itemId.split('ART-')[1];

        // FRAMES
        if (isProductADoorFrame(currentId)) {
          const { color, height } = getDoorFrameSize(currentId);

          if (height === DoorHeightEnum.Low) {
            if (lowDoorColors.indexOf(color) === -1) {
              lowDoorColors.push(color);
            }
          } else if (height === DoorHeightEnum.High) {
            if (highDoorColors.indexOf(color) === -1) {
              highDoorColors.push(color);
            }
          }
        }

        // RAILS
        if (isProductARail(currentId)) {
          const { color } = getRailSize(currentId);

          if (railColors.indexOf(color) === -1) {
            railColors.push(color);
          }
        }
      }

      return acc;
    },
    {
      invalidMandatories: [] as IShoppingProduct[],
      missingSkyttaProducts: {
        railColors: [] as DoorFrameColorEnum[],
        lowDoorColors: [] as DoorFrameColorEnum[],
        highDoorColors: [] as DoorFrameColorEnum[]
      }
    }
  );

  const { invalidMandatories, missingSkyttaProducts } = validationData;

  // Log errors
  const haveMissingMandatoryProducts = !!invalidMandatories.length;
  if (haveMissingMandatoryProducts) {
    const missingString = invalidMandatories.map(art => {
      return art.product.itemId;
    });

    captureException(new Error('Mandatory products are missing'), scope => {
      scope.setExtra('id', missingString);
      return scope;
    });
  }

  const numberOfRailsAvailable = TOTAL_NR_OF_COLORS - missingSkyttaProducts.railColors.length;

  const haveLessRailColorsThanRequired =
    numberOfRailsAvailable < REQUIRED_NUMBER_OF_COLORS_AVAILABLE;

  if (haveLessRailColorsThanRequired) {
    const missingString = missingSkyttaProducts.railColors.map(color => {
      return DoorFrameColorEnum[color];
    });

    captureException(new Error('Missing more SKYTTA rails colors than the required'), scope => {
      scope.setExtras({
        RequiredNumberOfColorsAvailable: REQUIRED_NUMBER_OF_COLORS_AVAILABLE,
        color: missingString
      });
      return scope;
    });
  }

  const doorHeightOptionsAvailable: DoorHeightEnum[] = [];

  const lowDoorColorsAvailable = TOTAL_NR_OF_COLORS - missingSkyttaProducts.lowDoorColors.length;
  if (lowDoorColorsAvailable >= REQUIRED_NUMBER_OF_COLORS_AVAILABLE) {
    doorHeightOptionsAvailable.push(DoorHeightEnum.Low);
  }

  const highDoorColorsAvailable = TOTAL_NR_OF_COLORS - missingSkyttaProducts.highDoorColors.length;
  if (highDoorColorsAvailable >= REQUIRED_NUMBER_OF_COLORS_AVAILABLE) {
    doorHeightOptionsAvailable.push(DoorHeightEnum.High);
  }

  const noDoorHeightOptionsAvailable = doorHeightOptionsAvailable.length === 0;

  const hasMandatoryProductsMissing =
    haveMissingMandatoryProducts || haveLessRailColorsThanRequired || noDoorHeightOptionsAvailable;

  return {
    hasMandatoryProductsMissing,
    missingSkyttaProducts,
    doorHeightOptionsAvailable
  };
};

export const verifyProduct = (product: IShoppingProduct, defaultProducts: string[]) => {
  if (!product?.product?.valid) {
    return false;
  }

  return defaultProducts.includes(product.product.itemId.split('ART-')[1]);
};

export const prependArt = (id: string) => {
  return `ART-${id}`;
};

export const prependSpr = (id: string) => {
  return `SPR-${id}`;
};

export const getIndexOfFirstImageMatchingType = (
  images: IProductImage[] | null,
  types: ImageTypeNameEnum[] | null
) => {
  if (!types || !images || !images.length) {
    return null;
  }

  for (const type of types) {
    for (const [index, image] of images.entries()) {
      if (image.size === ImageSizeTypeEnum.S5 && image.typeName === type) {
        return index;
      }
    }
  }

  return null;
};

// Temp solution until kompis fixes the product card image component we do best in handle this on our end
// https://i-p-e-x.slack.com/archives/CEY6PNS0Y/p1724832579489649
// https://github.com/inter-ikea/CBF-RE-IPEX-KOMPIS/blob/master/packages/kompis/component-product-card/src/components/product-card-media/ProductCardMedia.ts#L324
export const getFirstImageMatchingType = (
  images: IProductImage[] | null,
  types: ImageTypeNameEnum[] | null
): IProductImage | null => {
  if (!types || !images || !images.length) {
    return null;
  }

  for (const type of types) {
    for (const image of images) {
      if (image.size === ImageSizeTypeEnum.S5 && image.typeName == type) {
        return image;
      }
    }
  }

  return null;
};

export const getSolutionDescription = (
  productContent?: IProductContent | null
): string | undefined => {
  return `${productContent?.name}, ${productContent?.validDesignText}`;
};
